import React, { useCallback } from 'react';
import { Button, Typography } from '@hpstellar/core/visId';

import useCartState from '../../../../hooks/useCartState';
import useCheckoutCTA from './useCheckoutCTA';
import useDeviceInfo from '../../../../hooks/useDeviceInfo';
import { scrollToError as scrollToHandler } from '../../cart-items/useAlerts';

import AddToQuote from './add-to-quote';
import PaypalButton from './paypal-button';

import './checkout-container.less';

export const disableCheckout = (cartLevelErrorMessages, ctoVerification, orderitemIds) => {
    const accumulator = (
        acc,
        {
            message_createQuoteButtonDisabledMsg,
            createQuoteButtonDisabled,
            checkoutButtonDisabled,
            payPalButtonDisabled,
            message_checkOutDisableButtonMsg,
            message_mobileNo,
            message_ctaTextForMobile,
        },
    ) => {
        if (checkoutButtonDisabled === 'true') {
            acc.disabled = true;
        }
        if (message_checkOutDisableButtonMsg) {
            acc.errorMessage.push(message_checkOutDisableButtonMsg);
        }
        if (createQuoteButtonDisabled === 'false') {
            acc.createQuoteDisabled = false;
        }
        if (message_createQuoteButtonDisabledMsg) {
            acc.createQuoteButtonDisabledMsg = message_createQuoteButtonDisabledMsg;
        }
        if (message_mobileNo) {
            acc.contactCC = message_mobileNo;
        }
        if (message_ctaTextForMobile) {
            acc.ctaText = message_ctaTextForMobile;
        }
        if (payPalButtonDisabled === 'true') {
            acc.paypalDisabled = true;
        }
        return acc;
    };

    //first check if the cart has any errors
    const ctoErrors = Object.values(ctoVerification || {}).reduce(accumulator, {
        disabled: false,
        paypalDisabled: false,
        createQuoteDisabled: true,
        createQuoteButtonDisabledMsg: '',
        errorMessage: [],
    });

    //check for item level errors the prevent checkout
    Object.values(orderitemIds || {}).reduce((acc, { orderItemLevelErrorMessages }) => {
        return (orderItemLevelErrorMessages || []).reduce(accumulator, acc);
    }, ctoErrors);

    //then check cart level errors
    return cartLevelErrorMessages.reduce(accumulator, ctoErrors);
};

const CheckoutButton = props => {
    const {
        disabled,
        disclaimer = "If you don't have an HP account yet, you can create it now. You need to be registered in order to proceed.",
        csrActions,
        scrollToError,
        disableSignIn,
    } = props;
    const { updateLVOFlag } = csrActions || {};
    const { ctaType, checkoutUrl, loginUrl, registrationUrl } = useCheckoutCTA();

    const onClick = useCallback(() => {
        if (updateLVOFlag) {
            updateLVOFlag();
        }
        if (scrollToError) {
            scrollToError();
        }
    }, [updateLVOFlag, scrollToError]);

    if (ctaType === 'auth') {
        return !disableSignIn ? (
            <>
                <Button {...props} to={!disabled ? loginUrl : undefined}>
                    Sign in
                </Button>
                <div>
                    <Button
                        {...props}
                        to={!disabled ? registrationUrl : undefined}
                        variation="secondary"
                        onClick={disabled ? scrollToError : undefined}
                    >
                        Create an account
                    </Button>
                    {!disabled && (
                        <Typography className="checkout-disclaimer" variant="footnotes">
                            {disclaimer}
                        </Typography>
                    )}
                </div>
            </>
        ) : null;
    }

    return (
        <Button
            to={disabled ? undefined : checkoutUrl}
            {...props}
            disabled={!scrollToError && disabled}
            onClick={onClick}
        />
    );
};

export const CheckoutButtonContainer = ({
    buttonProps = { children: 'Checkout', fullWidth: true },
    csrActions,
    className,
    variation = 'vertical',
    payPalButtonProps,
}) => {
    const { cartInfo } = useCartState();
    const { device } = useDeviceInfo(['device'], 'default');
    const { cartDetails, ctoVerification = {} } = cartInfo || {};
    const { cartLevelErrorMessages = [], orderitemIds } = cartDetails || {};
    const { disabled, paypalDisabled, createQuoteDisabled, createQuoteButtonDisabledMsg, errorMessage } =
        disableCheckout(cartLevelErrorMessages, ctoVerification, orderitemIds);
    const header =
        variation !== 'horizontal' ? (
            <div className="divider">
                <span>or</span>
            </div>
        ) : null;
    const scrollToError = device === 'mobile' ? scrollToHandler : undefined;
    const checkoutBtn = (
        <CheckoutButton
            className="checkout-btn"
            {...buttonProps}
            disabled={disabled}
            csrActions={csrActions}
            scrollToError={scrollToError}
            disableSignIn={variation === 'horizontal'}
            size={variation === 'horizontal' ? 'small' : 'large'}
        />
    );
    const paypalBtn = (
        <PaypalButton
            disabled={disabled || paypalDisabled}
            header={header}
            showTAndC={variation !== 'horizontal'}
            scrollToError={scrollToError}
            {...payPalButtonProps}
        />
    );

    if (variation === 'horizontal') {
        return (
            <div className={`checkout-container horizontal${className ? ` ${className}` : ''}`}>
                {paypalBtn}
                {checkoutBtn}
            </div>
        );
    }

    return (
        <div className={`checkout-container${className ? ` ${className}` : ''}`}>
            {checkoutBtn}
            {errorMessage.map((message, index) => (
                <Typography key={index} variant="footnotes" className="error-message">
                    {message}
                </Typography>
            ))}
            <AddToQuote disabled={createQuoteDisabled} disclaimer={createQuoteButtonDisabledMsg} header={header} />
            {paypalBtn}
        </div>
    );
};

export default ({ csrActions, className, variation }) => {
    return <CheckoutButtonContainer csrActions={csrActions} className={className} variation={variation} />;
};
